import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const VILLA_IMAGE = "https://static.prod-images.emergentagent.com/jobs/38415ed3-3d9c-47e3-b9ab-3231a1029ca0/images/003ca9e39a67672c82fe8280bb3954d4fbaea7ee76692b7ddb0e963ecb37c0bf.png";

const COLORS = {
  vertForet: '#1e4a1a',
  orSable: '#c4a76c',
  ivoire: '#faf9f6',
};

const ManifesteBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['-4%', '4%']);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToProjects = () => {
    document.getElementById('projets')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="manifeste"
      ref={sectionRef}
      className="relative min-h-[85vh] lg:min-h-[90vh] overflow-hidden"
      style={{ backgroundColor: COLORS.ivoire }}
      data-testid="manifeste-section"
    >
      {/* Image — Right 65%, parallax */}
      <div className="absolute top-0 right-0 w-full lg:w-[65%] h-full overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: imageY }}>
          <img
            src={VILLA_IMAGE}
            alt="Villa de luxe moderne intégrée dans un paysage verdoyant"
            className="w-full h-[110%] object-cover"
            loading="lazy"
          />
        </motion.div>
        {/* Fade-out left edge on desktop */}
        <div
          className="absolute inset-y-0 left-0 w-40 hidden lg:block"
          style={{ background: `linear-gradient(to right, ${COLORS.ivoire}, transparent)` }}
        />
        {/* Fade-out bottom on mobile */}
        <div
          className="absolute inset-x-0 bottom-0 h-48 lg:hidden"
          style={{ background: `linear-gradient(to top, ${COLORS.ivoire}, transparent)` }}
        />
      </div>

      {/* Floating Ivoire InfoBox — Left, overlaps image */}
      <div className="relative z-10 min-h-[85vh] lg:min-h-[90vh] flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            className="relative w-full lg:w-[45%] lg:max-w-lg rounded-sm"
            style={{
              backgroundColor: 'rgba(250, 249, 246, 0.94)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '24px 24px 60px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.04)',
            }}
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="px-9 sm:px-11 py-14 sm:py-16">
              {/* Label */}
              <motion.div
                className="flex items-center gap-3 mb-8"
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <div className="w-7 h-px" style={{ backgroundColor: COLORS.orSable }} />
                <span
                  className="text-[11px] font-semibold tracking-[0.25em] uppercase"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: COLORS.orSable }}
                  data-testid="manifeste-label"
                >
                  Manifeste
                </span>
              </motion.div>

              {/* H2 Title */}
              <motion.h2
                className="font-display text-2xl sm:text-3xl lg:text-[2.1rem] leading-snug mb-6"
                style={{ color: COLORS.vertForet }}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.2 }}
                data-testid="manifeste-title"
              >
                Imaginer,{' '}
                <span className="block" style={{ color: COLORS.orSable }}>
                  développer, structurer.
                </span>
              </motion.h2>

              {/* Separator */}
              <motion.div
                className="w-10 h-px mb-7"
                style={{ backgroundColor: COLORS.orSable }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={isVisible ? { scaleX: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
              />

              {/* Body */}
              <motion.div
                className="text-sm sm:text-[15px] mb-10 space-y-4"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#4a5a47',
                  lineHeight: 1.8,
                }}
                initial={{ opacity: 0, y: 15 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.35 }}
                data-testid="manifeste-accroche"
              >
                <p>One Green Developpement imagine et développe des projets profondément ancrés dans leur territoire, où le paysage façonne l'architecture et où le foncier devient un levier stratégique de création de valeur.</p>
                <p>Depuis notre création, nous portons une ambition claire : transformer des territoires en lieux de vie organisés, sécurisés et valorisants, au service d'une croissance maîtrisée.</p>
              </motion.div>

              {/* CTA — Ghost style */}
              <motion.button
                onClick={() => window.location.href = '/adn-ogd'}
                className="group flex items-center gap-3 px-6 py-3 border text-sm font-medium uppercase tracking-[0.12em] transition-all duration-300"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: COLORS.vertForet,
                  borderColor: COLORS.vertForet,
                  borderRadius: '2px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = COLORS.vertForet;
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.vertForet;
                }}
                initial={{ opacity: 0, y: 15 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.45 }}
                data-testid="manifeste-cta"
              >
                Découvrir notre ADN
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
            </div>

            {/* Gold accent bar at top of card */}
            <div
              className="absolute top-0 left-9 right-9 h-px"
              style={{ backgroundColor: COLORS.orSable, opacity: 0.4 }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ManifesteBanner;
